import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Save, Eye, EyeOff, X, ImagePlus, RefreshCw } from "lucide-react";
import AdventDoor from "../components/AdventDoor";
import ChristmasCard from "../components/ChristmasCard";
import { absoluteUrl, adventThemeUrl } from "./Avvento";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "gioco-scoperta", label: "Gioco e scoperta" },
  { key: "relazione-cura", label: "Relazione e cura" },
  { key: "parole-comunicazione", label: "Parole e comunicazione" },
  { key: "autonomia", label: "Autonomia quotidiana" },
];

const THEMES = [
  { n: 1, field: "theme1_url", label: "Ambientazione 1", desc: "L'orsetto custode del Natale" },
  { n: 2, field: "theme2_url", label: "Ambientazione 2", desc: "L'orsetto nel bosco d'inverno" },
  { n: 3, field: "theme3_url", label: "Ambientazione 3", desc: "L'orsetto dei piccoli doni" },
];

// Date di sblocco: 1-24 dicembre 2026 alle 08:00 Europe/Rome (= 07:00 UTC dello stesso giorno)
const DEFAULT_PUBLISH_AT = (day) => {
  const d = new Date(Date.UTC(2026, 11, day, 7, 0, 0));
  return d.toISOString();
};

const EMPTY_DAY = (day) => ({
  day, title: "", category: "gioco-scoperta", age_range: "", intro: "",
  description: "", materials: "", variants: "", educational_value: "", safety_notes: "",
  image_url: "", pdf_url: "", theme: 0, day_status: "draft",
  publish_at: DEFAULT_PUBLISH_AT(day),
});

const effectiveTheme = (d) => (d.theme >= 1 && d.theme <= 3 ? d.theme : ((d.day - 1) % 3) + 1);

export default function AdminAvvento() {
  const [token] = useState(() => localStorage.getItem("tv_admin_token"));
  const [settings, setSettings] = useState(null);
  const [days, setDays] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [s, d] = await Promise.all([
        axios.get(`${API}/admin/advent/settings`, { headers }),
        axios.get(`${API}/admin/advent/days`, { headers }),
      ]);
      setSettings(s.data);
      // merge with 24 slots so every day exists in UI
      const byDay = Object.fromEntries(d.data.map((x) => [x.day, x]));
      const all = Array.from({ length: 24 }, (_, i) => ({ theme: 0, ...EMPTY_DAY(i + 1), ...byDay[i + 1] }));
      setDays(all);
    } catch (e) {
      if (e.response?.status === 401) window.location.href = "/admin/login";
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const togglePage = async () => {
    const next = !settings.page_published;
    await axios.put(`${API}/admin/advent/settings`, { ...settings, page_published: next }, { headers });
    setSettings({ ...settings, page_published: next });
  };

  const toggleRecap = async () => {
    const next = !settings.recap_published;
    await axios.put(`${API}/admin/advent/settings`, { ...settings, recap_published: next }, { headers });
    setSettings({ ...settings, recap_published: next });
  };

  const downloadRecapPreview = async () => {
    const r = await axios.get(`${API}/admin/advent/recap-preview.pdf`, { headers, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
  };

  const save = async (dayData) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/advent/days/${dayData.day}`, dayData, { headers });
      await load();
      setEditing(null);
    } finally { setSaving(false); }
  };

  const uploadFile = async (file, folder = "avvento") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    const r = await axios.post(`${API}/admin/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const saveAsset = async (field, url) => {
    const next = { ...settings, [field]: url };
    await axios.put(`${API}/admin/advent/settings`, next, { headers });
    setSettings(next);
  };

  if (!settings) return <div className="p-10 text-[#3D2140]/60">Caricamento…</div>;

  const published = days.filter((d) => d.day_status === "published").length;
  const draft = days.filter((d) => d.day_status === "draft").length;

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-6 md:p-10" data-testid="admin-avvento-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-semibold">Admin · Avvento 2026</p>
            <h1 className="font-display text-4xl text-[#3D2140]">24 giorni per crescere con cura</h1>
            <p className="text-sm text-[#3D2140]/60 mt-1">
              Pubblicati: <strong>{published}/24</strong> · Bozze: <strong>{draft}</strong>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={togglePage}
              data-testid="toggle-page-published"
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm ${settings.page_published ? "bg-[#81C3C9] text-[#3D2140]" : "bg-[#3D2140] text-white"}`}
            >
              {settings.page_published ? <><Eye size={16}/> Pagina ATTIVA (visibile)</> : <><EyeOff size={16}/> Pagina NON attiva</>}
            </button>
            <button
              onClick={toggleRecap}
              data-testid="toggle-recap-published"
              title="Il PDF diventa scaricabile pubblicamente solo dal 25 dicembre 2026 alle 8:00, se questo interruttore e la pagina sono attivi"
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm border-2 ${settings.recap_published ? "bg-[#FF2768] text-white border-[#FF2768]" : "bg-white text-[#3D2140] border-[#3D2140]/20"}`}
            >
              PDF riepilogo {settings.recap_published ? "ATTIVO" : "non attivo"} (sblocco 25 dic)
            </button>
            <button
              onClick={downloadRecapPreview}
              data-testid="download-recap-preview"
              title="Scarica subito il PDF completo: solo tu, con la tua password. Non accende nulla."
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm bg-[#3D2140] text-white"
            >
              Scarica anteprima PDF
            </button>
          </div>
        </div>

        {/* Risorse grafiche riutilizzabili: copertina + 3 ambientazioni + cartolina */}
        <AssetManager settings={settings} onUpload={uploadFile} onSave={saveAsset} />

        {/* Cartolina "Buon Natale, famiglie!" — messaggio modificabile + anteprima */}
        <ChristmasCardEditor settings={settings} onSave={saveAsset} />

        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-10">
          {days.map((d) => {
            const tUrl = adventThemeUrl(settings, effectiveTheme(d));
            return (
              <button
                key={d.day}
                onClick={() => setEditing(d)}
                data-testid={`admin-avvento-day-${d.day}`}
                className={`relative aspect-square rounded-xl overflow-hidden p-3 flex flex-col items-center justify-center text-center border-2 transition
                  ${d.day_status === "published" ? "border-[#81C3C9]" :
                    d.day_status === "ready" ? "border-[#FFE45C]" :
                    "border-[#3D2140]/10"}`}
              >
                {tUrl && <img src={tUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
                <span className="relative font-display text-3xl text-[#3D2140] drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)]">{d.day}</span>
                <span className="relative text-[10px] mt-1 text-[#3D2140] line-clamp-2 drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)]">{d.title || "— vuoto —"}</span>
                <span className="relative text-[9px] uppercase mt-1 tracking-wider text-[#3D2140]/70">{d.day_status}</span>
              </button>
            );
          })}
        </div>
      </div>

      {editing && (
        <DayEditor
          initial={editing}
          settings={settings}
          onClose={() => setEditing(null)}
          onSave={save}
          onUpload={uploadFile}
          saving={saving}
        />
      )}
    </div>
  );
}

function AssetManager({ settings, onUpload, onSave }) {
  const [busy, setBusy] = useState("");
  const assets = [
    { field: "cover_image_url", label: "Copertina ufficiale", desc: "24 giorni per crescere con cura — usata in pagina e nelle condivisioni" },
    ...THEMES.map((t) => ({ field: t.field, label: t.label, desc: t.desc })),
    { field: "christmas_card_image_url", label: "Cartolina di Natale", desc: "Buon Natale, famiglie! — appare da sola il 25 dicembre" },
  ];

  const handle = async (field, file) => {
    setBusy(field);
    try {
      const url = await onUpload(file, "avvento");
      await onSave(field, url);
    } catch (e) { alert("Errore upload: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#3D2140]/10 p-6 mb-10" data-testid="asset-manager">
      <h2 className="font-display text-2xl text-[#3D2140] mb-1">Risorse grafiche del calendario</h2>
      <p className="text-xs text-[#3D2140]/60 mb-5">Le immagini sono condivise da tutte le 24 caselle: sostituendo un'ambientazione si aggiornano automaticamente tutte le caselle che la usano.</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {assets.map((a) => (
          <AssetCard
            key={a.field}
            {...a}
            url={settings[a.field]}
            busy={busy === a.field}
            onFile={(f) => handle(a.field, f)}
          />
        ))}
      </div>
    </div>
  );
}

function ChristmasCardEditor({ settings, onSave }) {
  const [msg, setMsg] = useState(settings.christmas_card_message || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await onSave("christmas_card_message", msg); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#3D2140]/10 p-6 mb-10" data-testid="christmas-card-editor">
      <h2 className="font-display text-2xl text-[#3D2140] mb-1">Cartolina "Buon Natale, famiglie!"</h2>
      <p className="text-xs text-[#3D2140]/60 mb-5">Appare automaticamente sotto il calendario il 25 dicembre 2026 alle 8 del mattino (solo se la pagina è attiva). Prima di allora resta invisibile al pubblico. Nessuna promozione commerciale: solo il tuo saluto.</p>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="w-full md:w-72 shrink-0">
          <p className="text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-bold mb-2">Anteprima definitiva</p>
          <ChristmasCard imageUrl={absoluteUrl(settings.christmas_card_image_url)} message={msg} />
        </div>
        <div className="flex-1 w-full">
          <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-bold mb-1">Messaggio personalizzato</label>
          <textarea
            rows={5}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            data-testid="christmas-card-message-input"
            className="input"
          />
          <button
            onClick={save}
            disabled={saving}
            data-testid="christmas-card-save"
            className="mt-3 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold text-sm disabled:opacity-50"
          >
            <Save size={16} /> {saving ? "Salvo…" : "Salva messaggio"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetCard({ field, label, desc, url, busy, onFile }) {
  const inputRef = useRef(null);
  return (
    <div className="rounded-xl border border-[#3D2140]/10 overflow-hidden bg-[#FAF8F5]" data-testid={`asset-${field}`}>
      <div className="aspect-square bg-[#FFE4E8]/50 flex items-center justify-center overflow-hidden">
        {url ? (
          <img src={absoluteUrl(url)} alt={label} className="w-full h-full object-cover" />
        ) : (
          <ImagePlus className="text-[#3D2140]/30" size={28} />
        )}
      </div>
      <div className="p-3">
        <p className="text-xs font-bold text-[#3D2140]">{label}</p>
        <p className="text-[10px] text-[#3D2140]/60 mb-2 line-clamp-2">{desc}</p>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`asset-upload-${field}`}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#3D2140] text-white text-[11px] font-semibold hover:bg-[#5A3660] disabled:opacity-50"
        >
          <RefreshCw size={12} /> {busy ? "Carico…" : url ? "Sostituisci" : "Carica immagine"}
        </button>
      </div>
    </div>
  );
}

function DayEditor({ initial, settings, onClose, onSave, onUpload, saving }) {
  const [d, setD] = useState(initial);
  const upd = (k, v) => setD({ ...d, [k]: v });

  const handleUpload = async (file, kind) => {
    try {
      const url = await onUpload(file, "avvento");
      upd(kind === "image" ? "image_url" : "pdf_url", url);
    } catch (e) { alert("Errore upload: " + e.message); }
  };

  const themeUrl = adventThemeUrl(settings, effectiveTheme(d));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full my-8 p-8" data-testid="day-editor">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-3xl text-[#3D2140]">Giorno {d.day}</h2>
          <button onClick={onClose} className="text-[#3D2140]/60 hover:text-[#FF2768]"><X size={20} /></button>
        </div>

        {/* Anteprima definitiva della casella + scelta ambientazione */}
        <div className="flex flex-wrap items-start gap-5 bg-[#FAF8F5] rounded-xl p-4 mb-4">
          <div className="w-32 shrink-0">
            <AdventDoor day={d.day} title={d.title} themeUrl={themeUrl} unlocked testId="editor-door-preview" />
            <p className="text-[10px] text-center text-[#3D2140]/50 mt-1">Anteprima casella</p>
          </div>
          <div className="flex-1 min-w-[220px]">
            <Field label="Ambientazione grafica">
              <select value={d.theme || 0} onChange={(e) => upd("theme", Number(e.target.value))} className="input" data-testid="editor-theme">
                <option value={0}>Automatica (rotazione 1-2-3)</option>
                {THEMES.map((t) => <option key={t.n} value={t.n}>{t.label} — {t.desc}</option>)}
              </select>
              <p className="text-[10px] text-[#3D2140]/50 mt-1">Numero, titolo, font e impaginazione restano identici: cambia solo l'illustrazione di sfondo.</p>
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Titolo *">
            <input value={d.title} onChange={(e) => upd("title", e.target.value)} className="input" data-testid="editor-title" />
          </Field>
          <Field label="Categoria">
            <select value={d.category} onChange={(e) => upd("category", e.target.value)} className="input">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Fascia d'età">
            <input value={d.age_range} onChange={(e) => upd("age_range", e.target.value)} placeholder="es. 2-5 anni" className="input" />
          </Field>
          <Field label="Data e ora di apertura">
            <input type="datetime-local" value={toLocal(d.publish_at)} onChange={(e) => upd("publish_at", toISO(e.target.value))} className="input" data-testid="editor-publish-at" />
          </Field>
        </div>

        <Field label="Introduzione breve"><textarea rows={2} value={d.intro} onChange={(e) => upd("intro", e.target.value)} className="input" /></Field>
        <Field label="Descrizione della proposta"><textarea rows={5} value={d.description} onChange={(e) => upd("description", e.target.value)} className="input" /></Field>
        <Field label="Materiali"><textarea rows={2} value={d.materials} onChange={(e) => upd("materials", e.target.value)} className="input" /></Field>
        <Field label="Varianti per età"><textarea rows={2} value={d.variants} onChange={(e) => upd("variants", e.target.value)} className="input" /></Field>
        <Field label="Valore educativo"><textarea rows={2} value={d.educational_value} onChange={(e) => upd("educational_value", e.target.value)} className="input" /></Field>
        <Field label="Attenzioni per la sicurezza"><textarea rows={2} value={d.safety_notes} onChange={(e) => upd("safety_notes", e.target.value)} className="input" /></Field>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <Field label="Immagine extra (facoltativa, dentro la pagina del giorno)">
            <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], "image")} />
            {d.image_url && <img src={absoluteUrl(d.image_url)} className="mt-2 w-32 h-32 object-cover rounded-xl" alt="" />}
          </Field>
          <Field label="PDF (facoltativo)">
            <input type="file" accept=".pdf" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], "pdf")} />
            {d.pdf_url && <p className="text-xs text-[#3D2140]/60 mt-2 truncate">{d.pdf_url}</p>}
          </Field>
        </div>

        <Field label="Stato">
          <select value={d.day_status} onChange={(e) => upd("day_status", e.target.value)} className="input" data-testid="editor-status">
            <option value="draft">Bozza</option>
            <option value="ready">Pronto (programmato)</option>
            <option value="published">Pubblicato</option>
          </select>
        </Field>

        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-5 py-3 rounded-full border border-[#3D2140]/20 text-[#3D2140]">Annulla</button>
          <button onClick={() => onSave(d)} disabled={saving} data-testid="editor-save" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold disabled:opacity-50">
            <Save size={16} /> {saving ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
      <style>{`.input { width: 100%; padding: 10px 14px; border-radius: 10px; border: 1px solid rgba(61,33,64,0.15); background: #FAF8F5; color: #3D2140; } .input:focus { outline: none; box-shadow: 0 0 0 2px rgba(255,39,104,0.3); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3 md:mb-0">
      <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-bold mb-1 mt-3">{label}</label>
      {children}
    </div>
  );
}

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toISOString().slice(0, 16);
};
const toISO = (local) => {
  if (!local) return null;
  return new Date(local).toISOString();
};
